import {
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parse,
  startOfWeek,
} from "date-fns";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Event from "./Event";
import Weather from "./Weather";
import { legend, wakefieldLegend } from "./utils/getLocationSlug";
import getStartAndEndDate from "./utils/getStartAndEndDate";
import { useLocation } from "./utils/useLocation";
import useSheets from "./utils/useSheets";

export default function MonthView(props) {
  const history = useNavigate();
  const location = useLocation();
  const match = useParams<{ community: string; wing?: string }>();
  const { events, startDate, endDate, today } = useSheets({
    view: "month",
    date: props.date,
  });

  let extraEvents = [];

  if (props.location) {
    const extraEventsData = useSheets({
      view: "month",
      date: props.date,
      location: props.location,
    });

    extraEvents = extraEventsData.events;
  }

  const interval = eachWeekOfInterval({
    start: startDate,
    end: endDate,
  });

  let communityLegend = legend;

  if (match.community === "wakefield" || match.community === "apex") {
    communityLegend = wakefieldLegend;
  }

  return (
    <React.Fragment>
      <header className="header header--month">
        <div className="header__logo">
          <img src={`/img/logo.png`} alt="logo" />
        </div>
        <h1 className="header__title">Activities and Events: {format(today, "MMMM yyyy")}</h1>
        <div className="header__weather">
          <Weather noWeather />
        </div>
      </header>
      <div className={`calendar-layout ${match.community}`}>
        <main className={`calendar calendar--month ${props.location ? "calendar--location" : ""}`}>
          <div className="weekdays">
            <div className="weekdays__day">Sunday</div>
            <div className="weekdays__day">Monday</div>
            <div className="weekdays__day">Tuesday</div>
            <div className="weekdays__day">Wednesday</div>
            <div className="weekdays__day">Thursday</div>
            <div className="weekdays__day">Friday</div>
            <div className="weekdays__day">Saturday</div>
          </div>
          <div className="weeks">
            {interval.map((w, i) => {
              return (
                <div className="week" key={i}>
                  {eachDayOfInterval({
                    start: startOfWeek(w),
                    end: endOfWeek(w),
                  })
                    .filter((d) => {
                      if (d.getDate() === 1 && d.getMonth() === 4) {
                        return false;
                      }
                      return true;
                    })
                    .map((d, i) => {
                      const eventsForDay = [...events, ...extraEvents].filter((e) => {
                        const eventDate = parse(e["Event-Date"], "M/d/yy", new Date());

                        if (props.location) {
                          const location = communityLegend[props.location];

                          // console.log("location", location);

                          return (
                            isSameDay(eventDate, d) &&
                            (location.label === e["Event-Location"] ||
                              location.rooms.includes(e["Event-Location"]))
                          );
                        }

                        return (
                          isSameDay(eventDate, d) &&
                          e["Event-Name"] !== "Move In" &&
                          !e["Memory Care Calendar?"]
                        );
                      });

                      if (!isSameMonth(d, startDate)) {
                        return <div className="day day--empty" key={i}></div>;
                      }

                      return (
                        <div
                          className="day"
                          key={i}
                          onClick={() => {
                            const startDate = startOfWeek(d);
                            const dateString = format(startDate, "yyyy-MM-dd");
                            const link = location.pathname.replace("/month/next", "");
                            history(`${link}/week?date=${dateString}`);
                          }}
                        >
                          <div className="day__date">{d.getDate()}</div>
                          <div className="day__content">
                            {eventsForDay
                              .filter((e) => e["Holiday?"])
                              .map((ee, i) => (
                                <Event
                                  key={i}
                                  event={ee}
                                  date={d}
                                  locationIcons
                                  smallIcons
                                  noLocationTitle
                                  type="month"
                                  community={match.community}
                                />
                              ))}
                            {eventsForDay
                              .filter((e) => !e["Holiday?"] && !e["Hide on Monthly?"])
                              .sort((a, b) => {
                                const aDates = getStartAndEndDate(a);
                                const bDates = getStartAndEndDate(b);
                                return aDates.startDate.getTime() - bDates.startDate.getTime();
                              })
                              .map((ee, i) => (
                                <Event
                                  key={i}
                                  event={ee}
                                  date={d}
                                  locationIcons
                                  smallIcons
                                  noLocationTitle
                                  type="month"
                                  community={match.community}
                                />
                              ))}
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </main>

        <div className="nav">
          <div className="nav__legend">
            <div className="legend__title">All times subject to change.</div>
            <div className="legend__content">
              <div className="legend__item">
                <div className="legend__icon legend__icon--center"></div>
                <div className="legend__item-title">Center</div>
              </div>
              <div className="legend__item">
                <div className="legend__icon legend__icon--a-wing"></div>
                <div className="legend__item-title">A Wing</div>
              </div>

              {match.community !== "wakefield" && match.community !== "apex" && (
                <div className="legend__item">
                  <div className="legend__icon legend__icon--b-wing"></div>
                  <div className="legend__item-title">B Wing</div>
                </div>
              )}

              <div className="legend__item">
                <div className="legend__icon legend__icon--c-wing"></div>
                <div className="legend__item-title">C Wing</div>
              </div>

              {match.community !== "fuquay" &&
                match.community !== "wakefield" &&
                match.community !== "apex" && (
                  <div className="legend__item">
                    <div className="legend__icon legend__icon--d-wing"></div>
                    <div className="legend__item-title">D Wing</div>
                  </div>
                )}

              {(match.community === "wakefield" || match.community === "apex") && (
                <div className="legend__item">
                  <div className="legend__icon legend__icon--outdoor"></div>
                  <div className="legend__item-title">Outdoor</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* <div className="nav">
        <div className="nav__legend">
          <div className="legend__title">All times subject to change.</div>
          <div className="legend__content">
            <div className="legend__item">
              <div className="legend__icon legend__icon--center"></div>
              <div className="legend__item-title">Center</div>
            </div>
            <div className="legend__item">
              <div className="legend__icon legend__icon--a-wing"></div>
              <div className="legend__item-title">A Wing</div>
            </div>
            <div className="legend__item">
              <div className="legend__icon legend__icon--b-wing"></div>
              <div className="legend__item-title">B Wing</div>
            </div>
            <div className="legend__item">
              <div className="legend__icon legend__icon--c-wing"></div>
              <div className="legend__item-title">C Wing</div>
            </div>
            <div className="legend__item">
              <div className="legend__icon legend__icon--d-wing"></div>
              <div className="legend__item-title">D Wing</div>
            </div>
          </div>
        </div>
      </div> */}
    </React.Fragment>
  );
}
